import { times as _times } from 'lodash-es'
import { Model, Factory, belongsTo, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randPastDate,
  randNumber,
  randChanceBoolean,
  randFullName,
} from '@ngneat/falso'
import type { MockServer, MirageServer } from '../server.js'
import {
  pageable,
  filterable,
  createHtmlParagraph,
  generateRandomTimeString,
  getExistingEntity,
} from '../utils.js'

const DI_STATUSES = ['NOT_STARTED', 'PLANNED', 'PENDING', 'READY'] as const

const MODALITIES = ['X-ray', 'Ultrasound', 'MRI', 'CT', 'Doppler'] as const

const TARGET_AREAS = [
  'Upper neck (A)',
  'Lower Neck (A)',
  'Thorax (L)',
  'Abdomen (VD)',
  'Pelvis (L)',
  'Head (DV)',
] as const

const CATEGORIES = ['Head', 'Neck', 'Thorax', 'Abdomen', 'Pelvis'] as const

type WorklistAttrs = {
  date: string
  time: string | null
  status: string
  modalityId: string
  display_price: number
  price_breakdown: { items: { name: string; price: number }[]; total: number }
  pacs_url: string | null
  target_areas_display: string
  categories_display: string
  subcategories_display: string
}

export const model = (Model as ModelDefinition<WorklistAttrs>).extend({
  consultation: belongsTo(),
  user: belongsTo('user'),
})

export const factory = Factory.extend<Partial<WorklistAttrs>>({
  date() {
    return randPastDate({ years: 1 }).toISOString().split('T')[0]
  },
  time() {
    return randChanceBoolean({ chanceTrue: 0.7 })
      ? `${generateRandomTimeString()}:00`
      : null
  },
  status() {
    return rand([...DI_STATUSES])
  },
  modalityId() {
    return String(randNumber({ min: 1, max: MODALITIES.length }))
  },
  display_price() {
    return randNumber({ min: 20, max: 500 })
  },
  price_breakdown() {
    const count = randNumber({ min: 1, max: 3 })
    const items = _times(count, () => ({
      name: rand([...TARGET_AREAS]),
      price: randNumber({ min: 10, max: 200 }),
    }))
    const total = items.reduce((sum, item) => sum + item.price, 0)
    return { items, total }
  },
  pacs_url() {
    return randChanceBoolean({ chanceTrue: 0.4 })
      ? 'https://pacs.example.com/viewer?study=12345'
      : null
  },
  target_areas_display() {
    const count = randNumber({ min: 1, max: 3 })
    return [...TARGET_AREAS].slice(0, count).join(', ')
  },
  categories_display() {
    const count = randNumber({ min: 1, max: 2 })
    return [...CATEGORIES].slice(0, count).join(', ')
  },
  subcategories_display() {
    return ''
  },

  withUser: trait({
    afterCreate(worklist, server) {
      const user = getExistingEntity(server, 'user')
      worklist.update({ user })
    },
  }),

  afterCreate(worklist, server) {
    if (!worklist.consultation) {
      const consultation = getExistingEntity(server, 'consultation')
      worklist.update({ consultation })
    }
  },
})

function buildIncluded(worklist: {
  id?: string
  attrs?: WorklistAttrs & { userId?: string }
  user?: { id: string }
}) {
  const included: Record<string, unknown>[] = []
  const status = worklist.attrs?.status

  // Modality — use stable ID stored in attrs so filtering works correctly
  const modalityId = worklist.attrs?.modalityId ?? '1'
  const modalityIndex = Number(modalityId) - 1
  included.push({
    type: 'DiagnosticImagingModality',
    id: modalityId,
    attributes: {
      common_name: MODALITIES[modalityIndex] ?? MODALITIES[0],
    },
  })

  // Referral
  const referralId = String(randNumber({ min: 100, max: 999 }))
  included.push({
    type: 'DiagnosticImagingReferral',
    id: referralId,
    attributes: {
      id_display: referralId,
      referral_remarks: createHtmlParagraph(),
      diagnosis: randChanceBoolean({ chanceTrue: 0.5 })
        ? 'Suspected fracture'
        : null,
      internal_notes: randChanceBoolean({ chanceTrue: 0.3 })
        ? 'Follow up in 2 weeks'
        : null,
      answer_remarks: randChanceBoolean({ chanceTrue: 0.4 })
        ? createHtmlParagraph()
        : null,
      is_final: status === 'READY',
      status,
    },
    relationships: {
      status_logs: {
        data: [],
      },
    },
  })

  // User
  if (worklist.user || worklist.attrs?.userId) {
    const userId = worklist.user?.id || worklist.attrs?.userId
    included.push({
      type: 'User',
      id: String(userId),
      attributes: {
        first_name: randFullName().split(' ')[0],
        last_name: randFullName().split(' ')[1] || 'Smith',
        initials: 'JD',
      },
    })
  }

  // Status logs
  const statusOrder = ['NOT_STARTED', 'PLANNED', 'PENDING', 'READY']
  const currentIdx = statusOrder.indexOf(status as string)
  const logEntries: Array<{
    type: string
    id: string
    attributes: { status: string; changed: string }
    relationships: { changed_user: { data: { type: string; id: string } } }
  }> = []

  for (let i = 0; i <= currentIdx; i++) {
    const logId = `log-${worklist.id || randNumber({ min: 1, max: 999 })}-${i}`
    const logUserId = `log-user-${i}`
    logEntries.push({
      type: 'DiagnosticImagingStatusLog',
      id: logId,
      attributes: {
        status: statusOrder[i],
        changed: randPastDate({ years: 1 }).toISOString(),
      },
      relationships: {
        changed_user: {
          data: { type: 'User', id: logUserId },
        },
      },
    })
    included.push({
      type: 'User',
      id: logUserId,
      attributes: {
        first_name: randFullName().split(' ')[0],
        last_name: randFullName().split(' ')[1] || 'Doe',
        initials: 'XX',
      },
    })
    included.push(logEntries[logEntries.length - 1])
  }

  // Update referral status_logs relationship
  const referralResource = included.find(
    (r) => r.type === 'DiagnosticImagingReferral',
  )
  if (referralResource && 'relationships' in referralResource) {
    const rels = referralResource.relationships as Record<string, unknown>
    rels.status_logs = {
      data: logEntries.map((l) => ({ type: l.type, id: l.id })),
    }
  }

  return { included, modalityId, referralId }
}

export function routes(server: MockServer) {
  server.get('/diagnostic_imaging/modalities', function () {
    return {
      data: MODALITIES.map((name, index) => ({
        type: 'DiagnosticImagingModality',
        id: String(index + 1),
        attributes: { common_name: name },
      })),
    }
  })

  server.get(
    '/consultations/:parent_lookup_consultation/diagnostic_imaging_worklists',
    function (this: MirageServer, schema, request) {
      const consultationId = request.params.parent_lookup_consultation
      const worklists = schema.where('consultationDiagnosticImagingWorklist', {
        consultationId,
      })

      const filtered = filterable(worklists, request, {
        status: (item, value) => item.attrs.status === value,
        modality: (item, value) => item.attrs.modalityId === value,
        staff: (item, value) => String(item.attrs.userId) === value,
        search: (item, value) => {
          const search = value.toLowerCase()
          return (
            (item.attrs.target_areas_display || '')
              .toLowerCase()
              .includes(search) ||
            (item.attrs.categories_display || '').toLowerCase().includes(search)
          )
        },
      })

      const { data: paginatedData, meta } = pageable(filtered, request)

      const allIncluded: Record<string, unknown>[] = []
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const serializedData = paginatedData.models.map((worklist: any) => {
        const { included, modalityId, referralId } = buildIncluded(worklist)
        allIncluded.push(...included)

        return {
          type: 'ConsultationDiagnosticImagingWorklist',
          id: worklist.id || String(worklist.attrs?.id),
          attributes: {
            date: worklist.attrs?.date,
            time: worklist.attrs?.time,
            status: worklist.attrs?.status,
            display_price: worklist.attrs?.display_price,
            price_breakdown: worklist.attrs?.price_breakdown,
            pacs_url: worklist.attrs?.pacs_url,
            target_areas_display: worklist.attrs?.target_areas_display,
            categories_display: worklist.attrs?.categories_display,
            subcategories_display: worklist.attrs?.subcategories_display,
          },
          relationships: {
            modality: {
              data: { type: 'DiagnosticImagingModality', id: modalityId },
            },
            referral: {
              data: {
                type: 'DiagnosticImagingReferral',
                id: referralId,
              },
            },
            user: worklist.attrs?.userId
              ? {
                  data: {
                    type: 'User',
                    id: String(worklist.attrs.userId),
                  },
                }
              : { data: null },
          },
        }
      })

      return {
        data: serializedData,
        included: allIncluded,
        meta: { pagination: meta },
      }
    },
  )
}

export function seeds(server: MockServer) {
  const consultationIds = ['1', '2']

  consultationIds.forEach((consultationId) => {
    const consultation = server.schema.find('consultation', consultationId)
    if (!consultation) {
      return
    }
    const count = randNumber({ min: 2, max: 5 })
    for (let i = 0; i < count; i++) {
      server.create('consultationDiagnosticImagingWorklist', 'withUser', {
        consultation,
      })
    }
  })
}
